/**
 * S6 start-run form — pick a deployed agent instance (REQ-AIS-114).
 */

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioAgentInstance, CreateRunRequest } from '@/types/aiStudioAgent';

interface StartRunFormProps {
  runnableInstances: AiStudioAgentInstance[];
  submitting: boolean;
  onSubmit: (request: CreateRunRequest) => void;
  onCancel: () => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';
const HINT_CLASS = 'mt-1 text-base text-[var(--color-surface-text-secondary)]';

export const StartRunForm: React.FC<StartRunFormProps> = ({
  runnableInstances,
  submitting,
  onSubmit,
  onCancel,
}) => {
  const { t } = useLanguage();
  const [instanceId, setInstanceId] = useState('');
  const [runName, setRunName] = useState('');
  const [situationType, setSituationType] = useState('');
  const [scopeNotes, setScopeNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!instanceId || !runName.trim()) {
      setValidationError(t('aiStudio.runs.form.requiredFields'));
      return;
    }
    const instance = runnableInstances.find((row) => row.id === instanceId);
    if (instance?.status !== 'active') {
      setValidationError(t('aiStudio.runs.form.instanceNotActive'));
      return;
    }
    setValidationError(null);
    onSubmit({
      instanceId,
      runName: runName.trim(),
      situationType: situationType.trim() || null,
      scopeContext: scopeNotes.trim() ? { notes: scopeNotes.trim() } : {},
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4"
    >
      <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.runs.form.title')}
      </h2>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={LABEL_CLASS} htmlFor="run-instance">
            {t('aiStudio.runs.form.instance')}
          </label>
          <select
            id="run-instance"
            className={FIELD_CLASS}
            value={instanceId}
            disabled={submitting}
            onChange={(event) => setInstanceId(event.target.value)}
          >
            <option value="">{t('aiStudio.common.none')}</option>
            {runnableInstances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.instanceName}
                {instance.agentName ? ` · ${instance.agentName}` : ''}
              </option>
            ))}
          </select>
          <p className={HINT_CLASS}>{t('aiStudio.runs.form.instanceHint')}</p>
          {runnableInstances.length === 0 && (
            <p className="mt-1 text-base text-[var(--color-error)]">
              {t('aiStudio.runs.form.noRunnableInstances')}
            </p>
          )}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="run-name">
            {t('aiStudio.runs.form.runName')}
          </label>
          <input
            id="run-name"
            className={FIELD_CLASS}
            value={runName}
            maxLength={255}
            disabled={submitting}
            autoComplete="off"
            onChange={(event) => setRunName(event.target.value)}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="run-situation-type">
            {t('aiStudio.runs.form.situationType')}
          </label>
          <input
            id="run-situation-type"
            className={FIELD_CLASS}
            value={situationType}
            maxLength={64}
            disabled={submitting}
            autoComplete="off"
            onChange={(event) => setSituationType(event.target.value)}
          />
          <p className={HINT_CLASS}>{t('aiStudio.runs.form.situationTypeHint')}</p>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="run-scope-notes">
            {t('aiStudio.runs.form.scopeNotes')}
          </label>
          <input
            id="run-scope-notes"
            className={FIELD_CLASS}
            value={scopeNotes}
            disabled={submitting}
            autoComplete="off"
            onChange={(event) => setScopeNotes(event.target.value)}
          />
        </div>
      </div>

      {validationError && (
        <p role="alert" className="mt-3 text-base text-[var(--color-error)]">
          {validationError}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={submitting || runnableInstances.length === 0}
          className="rounded bg-[var(--color-success)] px-4 py-2 text-base text-white disabled:opacity-50"
        >
          {t('aiStudio.runs.startRun')}
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={onCancel}
          className="rounded border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)]"
        >
          {t('aiStudio.common.cancel')}
        </button>
      </div>
    </form>
  );
};
